import {createAction, createAsyncThunk} from '@reduxjs/toolkit';

import {ENDPOINTS} from 'constants/endpoints';
import {
    IsAutocompleteModeType,
    SuccessScreenType,
    DoctorDetailsAndUploadValidatedType,
    OrderedItemType,
} from 'desktop/pages/success/types/orderItemsTypes';
import {apiConfig, HTTP_REQUEST_HEADER} from 'libs/api/api';
import {logWithSentry} from 'libs/sentry/sentry';
import {convertDataToBackendFormat} from 'libs/success/prescription/prescription';
import {getStatusesUpdatedItems} from 'libs/success/statuses/statuses';
import {
    FieldType,
    ItemsType,
    FieldType as SuccessFieldType,
    VisiblyDetailsType,
} from 'types/successPage';

/**
 * Provides fill for one field of item which one need to be configured before send to backend (set upload file or doc details)
 */

export const changeField = createAction<FieldType>('SUCCESS_PAGE@CHANGE_FIELD');
/**
 *it changes isAutocmpleted item's prop
 */

export const setIsAutocompleteModeEnabled = createAction<IsAutocompleteModeType>(
    'SUCCESS_PAGE@SET_IS_AUTOCOMPLETE_MODE_ENABLED',
);
/**
 * it changes success screen
 */

export const changeSuccessScreen = createAction<SuccessScreenType>(
    'SUCCESS_PAGE@CHANGE_SUCCESS_SCREEN',
);
/**
 * it sets errors for success page
 */
export const setErrorsSuccessPage = createAction<DoctorDetailsAndUploadValidatedType>(
    'SUCCESS_PAGE@SET_ERRORS_SUCCESS_PAGE',
);
/**
 * It fetches items of order by increment id
 */

export const fetchOrderedItems = createAsyncThunk<{data: OrderedItemType[]}, string>(
    'SUCCESS_PAGE@FETCH_ORDERED_ITEMS',
    (incrementOrderId: string) =>
        apiConfig.instanceGusaBackend.request({
            headers: {
                [HTTP_REQUEST_HEADER.AUTHORIZATION]: '',
            },
            method: 'GET',
            url: `${ENDPOINTS.ORDER_GET_ITEMS({order: incrementOrderId})}`,
        }),
);
/**
 * It saves the order with new items
 */

export const saveOrder = createAsyncThunk<
    ItemsType,
    {items: SuccessFieldType[]; orderId: string; itemId: string}
>('SUCCESS_PAGE@SAVE_ORDER', async ({itemId, items, orderId}) => {
    const item = items.filter(orderItem => Number(orderItem.orderItemId) === Number(itemId));
    try {
        await apiConfig.instanceGusaBackend.request({
            data: {
                items: convertDataToBackendFormat(item),
            },
            headers: {
                [HTTP_REQUEST_HEADER.AUTHORIZATION]: '',
            },
            method: 'PUT',
            url: `${ENDPOINTS.CUSTOMER_GET_ORDER}/${orderId}`,
        });

        return getStatusesUpdatedItems(items);
    } catch (error) {
        const [currentError] = error?.response?.data?.messages?.error || [
            {message: 'Something went wrong.'},
        ];

        throw new Error(currentError.message);
    }
});
/**
 * It saves the order with new items
 */

export const postEyeExam = createAsyncThunk<
    ItemsType,
    {items: SuccessFieldType[]; orderId: number}
>('SUCCESS_PAGE@POST_EYE_EXAM', async ({items, orderId}) => {
    try {
        await apiConfig.instanceGusaBackend.request({
            data: {
                itemId: orderId,
            },
            headers: {
                [HTTP_REQUEST_HEADER.AUTHORIZATION]: '',
            },
            method: 'POST',
            url: ENDPOINTS.EXAM,
        });

        return getStatusesUpdatedItems(items);
    } catch (error) {
        const [currentError] = error?.response?.data?.messages?.error || [
            {message: 'Something went wrong.'},
        ];
        logWithSentry(error, 'TYP CL - Error while trying to get vision test link');

        throw new Error(currentError.message);
    }
});

export const fetchExamID = createAsyncThunk<{data: {id: number; token: string}}, {itemId: number}>(
    'SUCCESS_PAGE@GET_EXAM_ID',
    data =>
        apiConfig.instanceGusaBackend.request({
            data,
            method: 'post',
            url: ENDPOINTS.EXAM,
        }),
);

export const setVisiblyFields = createAction<Omit<VisiblyDetailsType, 'examID'>>(
    'SUCCESS_PAGE@SET_VISIBLY_FIELDS',
);
export const clearExamBind = createAction<void>('SUCCESS_PAGE@CLEAR_EXAM_BIND');

export const setVisibilyFailureMessage = createAction<string>(
    'SUCCESS_PAGE@SET_VISIBLY_FAILURE_MESSAGE',
);
